/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isBlank, isEmpty, isNone } from '@ember/utils';
import { dependentKeyCompat } from '@ember/object/compat';
import { TaskInstance } from 'ember-concurrency';
import AccountService from 'cio/services/account';
import PushService from 'cio/services/push';
import { Platform } from 'cio/utils/push/platforms';
import TemplateModel from 'cio/models/template';
import { RECIPIENT_FIELD } from 'cio/components/cio-send-test/content';

const EMPTY_BODY_VALIDATION_TYPES = ['twilio', 'slack'];
const URL_PREFIX_REGEX = /^(http|https):\/\//;

interface CioSendTestContentSubmitButtonArgs {
  type: string;
  recipient: string;
  isDisabled: boolean;
  platform?: Platform;
  sendTest: TaskInstance<unknown>;
  preview: Record<string, any>;
  template?: TemplateModel;
}

export default class CioSendTestContentSubmitButton extends Component<CioSendTestContentSubmitButtonArgs> {
  @service account!: AccountService;
  @service push!: PushService;

  @dependentKeyCompat
  get recipientIsBlank() {
    return this.args.type === 'webhook' ? false : isBlank(this.args.recipient);
  }

  get isDisabledDueToAccountVerification() {
    return this.args.type === 'webhook' && !this.account.current.verificationApproved;
  }

  @computed(
    'args.{type,platform,template.editor}',
    'args.preview.{body,body_json,title,url}',
    'recipientIsBlank',
    'push.platforms',
  )
  get sendButtonTooltip() {
    let { type, platform, preview, template } = this.args;

    if (isNone(preview)) {
      preview = {};
    }

    if (type === 'push') {
      if (isBlank(platform)) {
        if (isEmpty(this.push.platforms)) {
          return 'Please enable push actions';
        }

        return 'Please select a device type';
      }

      let editor;

      if (!isNone(template)) {
        editor = template.editor;
      }

      if (
        (editor === 'json' && isBlank(preview.body)) ||
        (editor !== 'json' && isBlank(preview.title) && isBlank(preview.body))
      ) {
        return 'Please add content before testing';
      }
    }

    if (EMPTY_BODY_VALIDATION_TYPES.includes(type) && isBlank(preview.body)) {
      return 'Please add content before testing';
    }

    if (type === 'webhook' && !URL_PREFIX_REGEX.test(preview.url)) {
      return 'URL must start with http:// or https://';
    }

    if (this.recipientIsBlank) {
      return `Please enter a ${RECIPIENT_FIELD[type]}`;
    }

    return;
  }

  get isDisabled() {
    return this.args.isDisabled ?? false;
  }

  get isSendDisabled() {
    /* 
      Webhooks send tests to the webhook's endpoint - need to be more careful about sending tests
      The isDisabled argument passed down includes permissions check
    */
    return (
      this.isDisabled ||
      !isNone(this.sendButtonTooltip) ||
      !this.account.current.confirmed ||
      this.isDisabledDueToAccountVerification ||
      this.args.isDisabled
    );
  }
}
